import './App.css';
import React, { useEffect, useRef } from 'react';
import { FaGithub, FaExternalLinkAlt } from 'react-icons/fa';
import { FaReact, FaNodeJs, FaDatabase, FaPython, FaJava, FaJs, FaHtml5, FaCss3Alt, FaDocker, FaGitAlt } from 'react-icons/fa';
import { SiExpress, SiFlask, SiPostgresql, SiJupyter, SiCplusplus, SiC, SiArduino, SiFirebase, SiTailwindcss, SiGodotengine, SiNextdotjs, SiStripe, SiSupabase } from 'react-icons/si';

const projects = [
  {
    title: 'Algal Bloom Risk Simulator',
    description: 'Built for MakeOHIO 2026: an ESP32 water-quality monitoring system that combines light, temperature, and humidity readings into a weighted bloom-risk score. Streams live telemetry over WebSocket to an interactive dashboard with risk visualizations and simulation controls.',
    tech: ['React', 'Vite', 'Node.js', 'Express', 'C++', 'ESP32', 'WebSocket'],
    link: 'https://github.com/royd2023/MakeOHIO2026',
    image: null
  },
  {
    title: 'CudaVision',
    description: 'A CUDA library with a public C++ API for GPU-accelerated image processing, matrix multiplication, and convolution. Benchmarked shared-memory tiling and constant-memory caching against naive kernels, achieving up to 13–16% faster runtimes for matrix multiplication and convolution.',
    tech: ['C++', 'CUDA', 'CMake'],
    link: 'https://github.com/royd2023/CudaVision',
    image: null
  },
  {
    title: 'Boxure',
    description: 'E-commerce platform for blind box collectibles with Stripe payments.',
    tech: ['React', 'Next.js', 'Node.js', 'PostgreSQL', 'Stripe'],
    link: 'https://github.com/4rd2/Boxure',
    image: null
  },
  {
    title: 'Spotify Remote',
    description: 'Arduino-based hardware remote for Spotify control via Bluetooth.',
    tech: ['Arduino', 'C++', 'ESP32', 'Bluetooth'],
    link: 'https://github.com/royd2023/SpotifyRemote',
    image: 'ece2360final.jpg'
  },
  {
    title: 'Furniture Detection',
    description: 'ML model comparing YOLOv8 with custom furniture detection.',
    tech: ['Python', 'YOLO', 'Computer Vision'],
    link: 'https://github.com/royd2023/Furniture-Detection',
    image: null
  },
  {
    title: 'Hand Detection App',
    description: 'Video app with gesture detection for Code4Cause2025.',
    tech: ['React', 'Python', 'OpenCV'],
    link: 'https://github.com/royd2023/Code4Cause2025',
    image: null
  },
  {
    title: 'Red Black Tree',
    description: 'Complete red-black tree implementation with CRUD operations.',
    tech: ['C++', 'Data Structures'],
    link: 'https://github.com/royd2023/RedBlackTree',
    image: 'redblacktree.jpg'
  },
  {
    title: 'FPS Game',
    description: 'First-person shooter with combat and enemy AI systems.',
    tech: ['Godot', 'GDScript'],
    link: 'https://github.com/royd2023/GodotFPS',
    image: null
  }
];

function App() {
  const experienceRef = useRef(null);
  const projectsRef = useRef(null);
  const skillsRef = useRef(null);
  const contactRef = useRef(null);
  const projectsTitleRef = useRef(null);
  const experienceTitleRef = useRef(null);
  const skillsTitleRef = useRef(null);
  const contactTitleRef = useRef(null);
  const contactContentRef = useRef(null);
  const heroRef = useRef(null);
  const [scrollProgress, setScrollProgress] = React.useState(0);
  const [showBackToTop, setShowBackToTop] = React.useState(false);
  const [cursorPosition, setCursorPosition] = React.useState({ x: 0, y: 0 });

  // Scroll progress tracking
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const progress = (scrollTop / scrollHeight) * 100;
      setScrollProgress(progress);

      // Show back to top button after scrolling down
      setShowBackToTop(scrollTop > 500);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Custom cursor tracking
  useEffect(() => {
    const handleMouseMove = (e) => {
      setCursorPosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate');
        }
      });
    }, observerOptions);

    // Observe section titles
    if (experienceTitleRef.current) observer.observe(experienceTitleRef.current);
    if (projectsTitleRef.current) observer.observe(projectsTitleRef.current);
    if (skillsTitleRef.current) observer.observe(skillsTitleRef.current);
    if (contactTitleRef.current) observer.observe(contactTitleRef.current);

    // Observe sections for line animations
    if (experienceRef.current) observer.observe(experienceRef.current);
    if (projectsRef.current) observer.observe(projectsRef.current);
    if (skillsRef.current) observer.observe(skillsRef.current);
    if (contactRef.current) observer.observe(contactRef.current);

    // Observe contact content
    if (contactContentRef.current) observer.observe(contactContentRef.current);

    // Observe project cards with staggered animation
    const projectCards = document.querySelectorAll('.project-card');
    projectCards.forEach((card, index) => {
      card.style.transitionDelay = `${index * 0.1}s`;
      observer.observe(card);
    });

    // Observe skill categories with staggered animation
    const skillCategories = document.querySelectorAll('.skill-category');
    skillCategories.forEach((category, index) => {
      category.style.transitionDelay = `${index * 0.1}s`;
      observer.observe(category);
    });

    return () => observer.disconnect();
  }, []);


  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="app-container">
      {/* Scroll Progress Bar */}
      <div className="scroll-progress" style={{ width: `${scrollProgress}%` }}></div>

      {/* Custom Cursor Trail */}
      <div
        className="cursor-trail"
        style={{
          left: `${cursorPosition.x}px`,
          top: `${cursorPosition.y}px`
        }}
      ></div>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button className="back-to-top" onClick={scrollToTop} aria-label="Back to top">
          ↑
        </button>
      )}

      {/* Header */}
      <header className="header">
        <div className="container">
          <h1 className="name">Roy Dinh</h1>
          <nav className="nav">
            <a href="https://github.com/royd2023" target="_blank" rel="noopener noreferrer" className="nav-link">
              <FaGithub /> GitHub
            </a>
            <a href="https://drive.google.com/file/d/1pK-H7N1TVTU82HiGQTP0eTQjLHxqjDGE/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="nav-link">
              Resume
            </a>
          </nav>
        </div>
      </header>

      {/* Hero Section - Modern Sharp Design */}
      <section className="hero-modern" ref={heroRef}>
        {/* Geometric Background Elements */}
        <div className="hero-bg-pattern">
          <div className="grid-overlay"></div>
          <div className="accent-line accent-line-1"></div>
          <div className="accent-line accent-line-2"></div>
          <div className="accent-line accent-line-3"></div>
          <div className="corner-accent top-left"></div>
          <div className="corner-accent top-right"></div>
          <div className="corner-accent bottom-left"></div>
          <div className="corner-accent bottom-right"></div>
        </div>

        <div className="container">
          <div className="hero-modern-content">
            {/* Main Content */}
            <div className="hero-main">
              <h1 className="hero-headline">
                <span className="headline-row">
                  <span className="text-reveal">Software</span>
                </span>
                <span className="headline-row">
                  <span className="text-reveal text-accent">Engineer</span>
                </span>
              </h1>

              <p className="hero-tagline">
                Experience in user experience and SWE practices. Interest in intersection between software and hardware.
              </p>

              <div className="hero-stats">
                <div className="stat-item">
                  <span className="stat-number">{projects.length}+</span>
                  <span className="stat-label">Projects</span>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                  <span className="stat-number">2027</span>
                  <span className="stat-label">Graduation</span>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                  <span className="stat-number">OSU</span>
                  <span className="stat-label">University</span>
                </div>
              </div>

              <div className="hero-cta">
                <a href="#projects" className="cta-primary">
                  View Projects
                  <span className="cta-arrow">→</span>
                </a>
                <a href="https://drive.google.com/file/d/1VDu4AyAr6Pk6ud6BluNnu5tiaZj5Po97/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="cta-secondary">
                  Resume
                </a>
              </div>
            </div>

          </div>

          {/* Bottom Section */}
          <div className="hero-bottom">
            <div className="current-focus">
              <span className="focus-label">Currently</span>
              <span className="focus-text">Studying CUDA • Research Assistant • Collaborating with John Hopkins APL</span>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="experience-section" id="experience" ref={experienceRef}>
        <div className="container">
          <h2 className="section-title" ref={experienceTitleRef}>Experience</h2>
          <article className="experience-card">
            <div className="experience-header">
              <div>
                <h3 className="experience-role">Software Engineer Intern</h3>
                <p className="experience-company">DwyerOmega (Lakeshore Cryotronics)</p>
              </div>
              <div className="experience-meta">
                <p>May – August 2026</p>
                <p>Westerville, OH</p>
              </div>
            </div>
            <ul className="experience-highlights">
              <li>Developed 10+ features and bug fixes in C#/.NET for the Model 346 Temperature Controller and M81 Synchronous Source Measure System, building on-instrument interfaces and integrating .NET with embedded firmware through Lake Shore’s protocol.</li>
              <li>Validated changes across the full stack with test cases, .NET debugging in Visual Studio, and embedded C++ flashing and debugging on instruments through Microchip Studio.</li>
              <li>Worked in two-week Agile/Scrum sprints, managed feature branches in Azure DevOps, and refined pull requests through peer code review.</li>
              <li>Improved interface clarity and user experience through iterative feedback from application scientists and the product manager.</li>
            </ul>
            <div className="experience-tech">
              {['C#', '.NET', 'C++', 'Azure DevOps', 'Visual Studio', 'Microchip Studio'].map((tech) => (
                <span key={tech} className="tech-tag">{tech}</span>
              ))}
            </div>
          </article>
        </div>
      </section>

      {/* Projects Section */}
      <section className="projects-section" id="projects" ref={projectsRef}>
        <div className="container">
          <h2 className="section-title" ref={projectsTitleRef}>Projects</h2>
          <div className="projects-grid">
            {projects.map((project, index) => (
              <div key={project.title} className="project-card">
                {project.image && (
                  <div className="project-image-container">
                    <img src={project.image} alt={project.title} className="project-image" />
                  </div>
                )}
                <div className="project-content">
                  <h3 className="project-title">{project.title}</h3>
                  <p className="project-description">{project.description}</p>
                  <div className="project-tech">
                    {project.tech.map((tech) => (
                      <span key={tech} className="tech-tag">{tech}</span>
                    ))}
                  </div>
                  {project.link && <div className="project-links">
                    <a 
                      href={project.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="project-link"
                    >
                      <FaGithub /> View Code
                    </a>
                  </div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="skills-section" ref={skillsRef}>
        <div className="container">
          <h2 className="section-title" ref={skillsTitleRef}>Skills & Technologies</h2>
          <div className="skills-grid">
            <div className="skill-category">
              <h3>Languages</h3>
              <div className="skill-items">
                <span className="skill-item magnetic-item"><FaPython /> Python</span>
                <span className="skill-item magnetic-item"><FaJs /> JavaScript</span>
                <span className="skill-item magnetic-item"><SiCplusplus /> C++</span>
                <span className="skill-item magnetic-item"><FaJava /> Java</span>
                <span className="skill-item magnetic-item"><SiC /> C</span>
                <span className="skill-item magnetic-item"><FaDatabase /> SQL</span>
              </div>
            </div>
            <div className="skill-category">
              <h3>Frontend</h3>
              <div className="skill-items">
                <span className="skill-item magnetic-item"><FaReact /> React</span>
                <span className="skill-item magnetic-item"><SiNextdotjs /> Next.js</span>
                <span className="skill-item magnetic-item"><FaHtml5 /> HTML5</span>
                <span className="skill-item magnetic-item"><FaCss3Alt /> CSS3</span>
                <span className="skill-item magnetic-item"><SiTailwindcss /> Tailwind CSS</span>
              </div>
            </div>
            <div className="skill-category">
              <h3>Backend</h3>
              <div className="skill-items">
                <span className="skill-item magnetic-item"><FaNodeJs /> Node.js</span>
                <span className="skill-item magnetic-item"><SiExpress /> Express.js</span>
                <span className="skill-item magnetic-item"><SiFlask /> Flask</span>
                <span className="skill-item magnetic-item"><SiPostgresql /> PostgreSQL</span>
                <span className="skill-item magnetic-item"><SiFirebase /> Firebase</span>
                <span className="skill-item magnetic-item"><SiSupabase /> Supabase</span>
              </div>
            </div>
            <div className="skill-category">
              <h3>Tools & Others</h3>
              <div className="skill-items">
                <span className="skill-item magnetic-item"><FaGitAlt /> Git</span>
                <span className="skill-item magnetic-item"><FaDocker /> Docker</span>
                <span className="skill-item magnetic-item"><SiArduino /> Arduino</span>
                <span className="skill-item magnetic-item"><SiGodotengine /> Godot</span>
                <span className="skill-item magnetic-item"><SiJupyter /> Jupyter</span>
                <span className="skill-item magnetic-item"><SiStripe /> Stripe</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section" ref={contactRef}>
        <div className="container">
          <div className="contact-content" ref={contactContentRef}>
            <h2 className="section-title" ref={contactTitleRef}>Let's Work Together</h2>
            <p className="contact-description">
              Have a question or want to work together? I'm always interested in new opportunities and collaborations.
            </p>
            <div className="contact-links">
              <a 
                href="https://mail.google.com/mail/?view=cm&fs=1&to=rdinh2023@gmail.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="contact-link"
              >
                <FaExternalLinkAlt /> Email Me
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Section */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-left">
              <h3 className="footer-name">Roy Dinh</h3>
              <p className="footer-tagline">Future Software Developer & CS Student</p>
            </div>
            <div className="footer-links">
              <a href="https://github.com/royd2023" target="_blank" rel="noopener noreferrer" className="footer-link">
                <FaGithub /> GitHub
              </a>
              <a href="mailto:rdinh2023@gmail.com" className="footer-link">
                <FaExternalLinkAlt /> Email
              </a>
              <a href="https://drive.google.com/file/d/1pK-H7N1TVTU82HiGQTP0eTQjLHxqjDGE/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="footer-link">
                Resume
              </a>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 Roy Dinh</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
